import Button from '@mui/material/Button';
import { useContext, useState, useEffect, useMemo } from 'react';
import { UserContext } from '../context/UserContext';
import { ROUTES } from '../constants';
import { Store } from 'react-notifications-component';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PropTypes from 'prop-types';

const DISMISSED_CERTIFICATIONS_KEY = 'dismissed-exp-certifications';
const SESSION_SEEN_KEY = 'cert-session-seen';
const SESSION_AUTH_KEY = 'cert-session-auth';

// Restituisce un ID univoco per la sessione Keycloak corrente:
// session_state è stabile nei refresh token ma cambia ad ogni nuovo login.
function getAuthSessionId() {
  const token = localStorage.getItem('kc_token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const sessionMarker = payload.session_state || payload.jti;
    return sessionMarker ? `${payload.sub}:${sessionMarker}` : null;
  } catch {
    return null;
  }
}

function getCertKey(cert) {
  return `${cert.id}:${cert.updated_at ?? ''}`;
}

const NOTIFICATION_CONFIG = {
  type: 'default',
  className: 'custom-cert-alert',
  insert: 'top',
  container: 'top-right',
  animationIn: ['animate__animated', 'animate__fadeIn'],
  animationOut: ['animate__animated', 'animate__fadeOut'],
  dismiss: {
    duration: 0,
    onScreen: true,
    showIcon: false,
    click: false,
  },
};

const STYLES = {
  icon: {
    fontSize: 22,
    color: '#EA9607',
    flexShrink: 0,
    marginTop: '2px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  titleWrapper: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3D3D',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: '12px',
    color: '#666666',
    margin: '2px 0 0',
  },
  closeButton: {
    flexShrink: 0,
    minWidth: 0,
    padding: '0px',
    color: '#955F03',
  },
  actionsRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  button: {
    fontWeight: 600,
    textTransform: 'none',
    color: '#955F03',
    fontSize: '13px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
};

const CertificationNotificationContent = ({
  cert,
  onViewClick,
  onDismissClick,
  onCloseTemporarily,
}) => (
  <div
    style={{
      width: '100%',
      padding: '12px 14px',
      backgroundColor: '#F5EFE0',
      borderRadius: '8px',
      borderLeft: '4px solid #EA9607',
      boxSizing: 'border-box',
    }}
  >
    <div style={STYLES.headerRow}>
      <WarningAmberRoundedIcon style={STYLES.icon} />
      <div style={STYLES.titleWrapper}>
        <div style={STYLES.title}>{cert.certification__description}</div>
        <div style={STYLES.subtitle}>Certificazione scaduta o in scadenza</div>
      </div>
      <Button
        variant="text"
        size="small"
        onClick={onCloseTemporarily}
        sx={STYLES.closeButton}
      >
        <CloseRoundedIcon fontSize="small" />
      </Button>
    </div>
    <div style={STYLES.actionsRow}>
      <Button
        variant="text"
        size="small"
        onClick={onViewClick}
        sx={STYLES.button}
      >
        Vedi
      </Button>
      <Button
        variant="text"
        size="small"
        onClick={onDismissClick}
        sx={STYLES.button}
      >
        Non mostrare più
      </Button>
    </div>
  </div>
);

CertificationNotificationContent.propTypes = {
  cert: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    certification__description: PropTypes.string.isRequired,
  }).isRequired,
  onViewClick: PropTypes.func.isRequired,
  onDismissClick: PropTypes.func.isRequired,
  onCloseTemporarily: PropTypes.func.isRequired,
};

export const EarnedCertificationsAlert = () => {
  const { user } = useContext(UserContext);
  const [dismissedCertifications, setDismissedCertifications] = useState([]);
  const [sessionSeenCertifications, setSessionSeenCertifications] = useState([]);
  const [temporarilyClosed, setTemporarilyClosed] = useState([]);
  const [notificationIds, setNotificationIds] = useState({});

  // Carica le certificazioni dismissate dal localStorage
  useEffect(() => {
    const raw = localStorage.getItem(DISMISSED_CERTIFICATIONS_KEY);
    if (raw) {
      // Normalizza: assicura che tutti i valori siano stringhe e rimuove duplicati
      const parsed = JSON.parse(raw);
      const normalized = [...new Set(parsed.map(String))];
      if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
        localStorage.setItem(
          DISMISSED_CERTIFICATIONS_KEY,
          JSON.stringify(normalized)
        );
      }
      setDismissedCertifications(normalized);
    } else {
      setDismissedCertifications([]);
    }
  }, []);

  // Carica le certificazioni viste questa sessione da sessionStorage.
  // Se il session_state Keycloak è cambiato (nuovo login), invalida i dati precedenti.
  useEffect(() => {
    const currentSessionId = getAuthSessionId();
    const storedSessionId = sessionStorage.getItem(SESSION_AUTH_KEY);

    if (!currentSessionId || currentSessionId !== storedSessionId) {
      sessionStorage.removeItem(SESSION_SEEN_KEY);
      if (currentSessionId) {
        sessionStorage.setItem(SESSION_AUTH_KEY, currentSessionId);
      }
      return;
    }

    const raw = sessionStorage.getItem(SESSION_SEEN_KEY);
    if (raw) {
      try {
        setSessionSeenCertifications(JSON.parse(raw));
      } catch {
        setSessionSeenCertifications([]);
      }
    }
  }, []);

  // Segna una certificazione come vista in questa sessione (chiave composta id:updated_at)
  const markSessionSeen = (certKey) => {
    setSessionSeenCertifications((prev) => {
      if (prev.includes(certKey)) return prev;
      const updated = [...prev, certKey];
      sessionStorage.setItem(SESSION_SEEN_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  // Salva una certificazione come dismissata (chiave composta id:updated_at)
  const markDismissed = (certKey) => {
    setDismissedCertifications((prev) => {
      if (prev.includes(certKey)) return prev;
      const updated = [...prev, certKey];
      localStorage.setItem(
        DISMISSED_CERTIFICATIONS_KEY,
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  // Rimuove una notifica
  const removeNotification = (certificationId, notificationId) => {
    if (notificationId) {
      Store.removeNotification(notificationId);
    }

    setNotificationIds((prev) => {
      const next = { ...prev };
      delete next[certificationId];
      return next;
    });
  };

  // Handler per il click su "Vedi"
  const handleViewClick = (cert, notificationId) => {
    markSessionSeen(getCertKey(cert));
    removeNotification(cert.id, notificationId);
    window.location.assign(ROUTES.EARNED_CERTIFICATIONS);
  };

  // Handler per il click su "Non mostrare più"
  const handleDismissClick = (cert, notificationId) => {
    markDismissed(getCertKey(cert));
    removeNotification(cert.id, notificationId);
  };

  // Handler per chiudere temporaneamente (senza dismissare)
  const handleCloseTemporarily = (certificationId, notificationId) => {
    setTemporarilyClosed((prev) => [...prev, certificationId]);
    removeNotification(certificationId, notificationId);
  };

  // Filtra le certificazioni visibili
  const visibleCertifications = useMemo(
    () =>
      Array.isArray(user?.exp_certifications)
        ? user.exp_certifications.filter(
            (cert) =>
              !dismissedCertifications.includes(getCertKey(cert)) &&
              !sessionSeenCertifications.includes(getCertKey(cert)) &&
              !temporarilyClosed.includes(cert.id)
          )
        : [],
    [user?.exp_certifications, dismissedCertifications, sessionSeenCertifications, temporarilyClosed]
  );

  // Mostra le notifiche per le certificazioni visibili
  useEffect(() => {
    visibleCertifications.forEach((cert) => {
      // Salta se la notifica è già stata creata
      if (notificationIds[cert.id]) return;

      const notificationId = Store.addNotification({
        ...NOTIFICATION_CONFIG,
        width: Math.floor(window.innerWidth / 4),
        content: (
          <CertificationNotificationContent
            cert={cert}
            onViewClick={() => handleViewClick(cert, notificationId)}
            onDismissClick={() => handleDismissClick(cert, notificationId)}
            onCloseTemporarily={() =>
              handleCloseTemporarily(cert.id, notificationId)
            }
          />
        ),
      });

      setNotificationIds((prev) => ({
        ...prev,
        [cert.id]: notificationId,
      }));
    });
  }, [visibleCertifications]);

  return null;
};
